import {
    ProjectId,
    TestObjectCollectionId,
    TestObjectId
} from '@webmate-web-core/shared/types/ids';
import {
    TestObjectCollection,
    TestObjectCollectionMemberRef,
    TestObjectCollectionStatus,
    TestObjectCollectionSummary,
    TestObjectCollectionUpdateAction
} from '@webmate-web-core/shared/types/testmgmt';

export const NEW_TESTOBJECT_COLLECTION_PLACEHOLDER_ID = '__new_testobject_collection__' as TestObjectCollectionId;

export interface TestObjectCollectionDraft {
    name: string;
    description?: string;
    status: Exclude<TestObjectCollectionStatus, 'deleted'>;
    members: TestObjectCollectionMemberRef[];
}

export interface ContainingTestObjectCollection {
    collectionId: TestObjectCollectionId;
    projectId: ProjectId;
    name: string;
    description?: string;
    status: TestObjectCollectionStatus;
    matchingRefs: TestObjectCollectionMemberRef[];
    membershipLabels: string[];
}

export function getTestObjectCollectionMemberBaseId(memberRef: TestObjectCollectionMemberRef): TestObjectId {
    return memberRef.split('@', 1)[0] as TestObjectId;
}

export function getTestObjectCollectionMemberLabel(memberRef: TestObjectCollectionMemberRef): string {
    const version = memberRef.includes('@') ? memberRef.slice(memberRef.indexOf('@') + 1) : undefined;
    return version ? `Pinned v${version}` : 'Latest';
}

export function buildContainingTestObjectCollections(testObjectId: TestObjectId, collections: TestObjectCollection[]): ContainingTestObjectCollection[] {
    return (collections || [])
        .filter(collection => collection.status !== 'deleted')
        .map(collection => {
            const matchingRefs = (collection.members || []).filter(memberRef => getTestObjectCollectionMemberBaseId(memberRef) === testObjectId);
            if (matchingRefs.length === 0) {
                return undefined;
            }

            return {
                collectionId: collection.collectionId,
                projectId: collection.projectId,
                name: collection.name,
                description: collection.description,
                status: collection.status,
                matchingRefs,
                membershipLabels: matchingRefs.map(getTestObjectCollectionMemberLabel)
            } as ContainingTestObjectCollection;
        })
        .filter((collection): collection is ContainingTestObjectCollection => !!collection)
        .sort((left, right) => {
            if (left.status !== right.status) {
                return left.status === 'active' ? -1 : 1;
            }

            return left.name.localeCompare(right.name);
        });
}

export function createEmptyTestObjectCollectionDraft(): TestObjectCollectionDraft {
    return {
        name: '',
        description: undefined,
        status: 'active',
        members: []
    };
}

export function createTestObjectCollectionDraft(collection: TestObjectCollection): TestObjectCollectionDraft {
    return {
        name: collection.name,
        description: collection.description,
        status: collection.status === 'deleted' ? 'active' : collection.status,
        members: [...collection.members]
    };
}

export function normalizeTestObjectCollectionName(name: string): string {
    return (name || '').trim();
}

export function normalizeTestObjectCollectionDescription(description?: string): string | undefined {
    const normalized = (description || '').trim();
    return normalized.length > 0 ? normalized : undefined;
}

export function normalizeTestObjectCollectionDraft(draft: TestObjectCollectionDraft): TestObjectCollectionDraft {
    return {
        name: normalizeTestObjectCollectionName(draft.name),
        description: normalizeTestObjectCollectionDescription(draft.description),
        status: draft.status,
        members: [...(draft.members || [])]
    };
}

export function areTestObjectCollectionDraftsEqual(left: TestObjectCollectionDraft, right: TestObjectCollectionDraft): boolean {
    const normalizedLeft = normalizeTestObjectCollectionDraft(left);
    const normalizedRight = normalizeTestObjectCollectionDraft(right);

    return normalizedLeft.name === normalizedRight.name
        && normalizedLeft.description === normalizedRight.description
        && normalizedLeft.status === normalizedRight.status
        && normalizedLeft.members.length === normalizedRight.members.length
        && normalizedLeft.members.every((member, index) => member === normalizedRight.members[index]);
}

export function toTestObjectCollectionSummary(collection: TestObjectCollection): TestObjectCollectionSummary {
    return {
        collectionId: collection.collectionId,
        projectId: collection.projectId,
        name: collection.name,
        description: collection.description,
        memberCount: collection.members.length,
        status: collection.status
    };
}

export function buildTestObjectCollectionUpdateActions(original: TestObjectCollection, draft: TestObjectCollectionDraft): TestObjectCollectionUpdateAction[] {
    const normalizedDraft = normalizeTestObjectCollectionDraft(draft);
    const originalMembers = original.members || [];
    const draftMembers = normalizedDraft.members || [];
    const originalMembersSet = new Set(originalMembers);
    const draftMembersSet = new Set(draftMembers);
    const actions: TestObjectCollectionUpdateAction[] = [];

    if (normalizedDraft.name !== original.name) {
        actions.push({
            type: 'SetName',
            name: normalizedDraft.name
        });
    }

    if (normalizeTestObjectCollectionDescription(original.description) !== normalizedDraft.description) {
        actions.push({
            type: 'SetDescription',
            description: normalizedDraft.description
        });
    }

    draftMembers.forEach(member => {
        if (!originalMembersSet.has(member)) {
            actions.push({
                type: 'AddMember',
                member
            });
        }
    });

    originalMembers.forEach(member => {
        if (!draftMembersSet.has(member)) {
            actions.push({
                type: 'RemoveMember',
                member
            });
        }
    });

    const membershipChanged = draftMembers.length !== originalMembers.length
        || draftMembers.some(member => !originalMembersSet.has(member))
        || originalMembers.some(member => !draftMembersSet.has(member));
    const orderChanged = originalMembers.length === draftMembers.length
        && originalMembers.some((member, index) => member !== draftMembers[index]);

    if (membershipChanged || orderChanged) {
        actions.push({
            type: 'ReorderMembers',
            members: [...draftMembers]
        });
    }

    if (normalizedDraft.status !== original.status) {
        actions.push({
            type: normalizedDraft.status === 'archived' ? 'Archive' : 'Activate'
        });
    }

    return actions;
}
